"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, CircleAlert as AlertTriangle, ArrowUpRight, BellRing, BookOpen, Compass, Dog, Eye, Heart, House as Home, Moon, MessageSquare, ShieldAlert, Sparkles, Users, Volume2, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface TopicItem {
  id: string;
  title: string;
  category: string;
  description: string;
  takeaway: string;
  iconName: string;
}
const TOPICS: TopicItem[] = [{
  id: "dog-behavior",
  title: "Dog behavior",
  category: "Canine Foundation",
  description: "Understand instinctual canine patterns, emotional baselines, and environmental triggers.",
  takeaway: "Core focus: desensitization and routine consistency.",
  iconName: "Dog"
}, {
  id: "cat-behavior",
  title: "Cat behavior",
  category: "Feline Foundation",
  description: "Decode feline territorial needs, stress markers, and species-specific communication.",
  takeaway: "Core focus: environmental enrichment and resource security.",
  iconName: "Compass"
}, {
  id: "dog-training",
  title: "Dog training",
  category: "Canine Conditioning",
  description: "Structured marker training and positive reinforcement foundations for reliable recall and cues.",
  takeaway: "Core focus: high-value reward timing without force.",
  iconName: "Sparkles"
}, {
  id: "puppy-training",
  title: "Puppy training",
  category: "Early Development",
  description: "Critical development stages, bite inhibition protocols, and initial cue acquisition.",
  takeaway: "Core focus: gentle exposure during the 8–16 week window.",
  iconName: "Heart"
}, {
  id: "anxiety",
  title: "General anxiety",
  category: "Clinical Wellness",
  description: "Identify subtle chronic stress indicators, noise phobias, and sensory overstimulation.",
  takeaway: "Core focus: safe zones and veterinary behavioral consultation.",
  iconName: "ShieldAlert"
}, {
  id: "separation-anxiety",
  title: "Separation anxiety",
  category: "Clinical Wellness",
  description: "Systematic sub-threshold absence conditioning to help pets cope calmly while alone.",
  takeaway: "Core focus: gradual threshold steps and counter-conditioning.",
  iconName: "Home"
}, {
  id: "barking",
  title: "Excessive barking",
  category: "Canine Vocalization",
  description: "Distinguish between alert, boredom, and demand vocalization to treat the root cause.",
  takeaway: "Core focus: differential reinforcement of alternative behaviors.",
  iconName: "Volume2"
}, {
  id: "meowing",
  title: "Excessive meowing",
  category: "Feline Vocalization",
  description: "Assess medical conditions, cognitive changes, nocturnal crying, and attention-seeking cues.",
  takeaway: "Core focus: routine evening play and veterinary health screening.",
  iconName: "MessageSquare"
}, {
  id: "scratching",
  title: "Destructive scratching",
  category: "Feline Instinct",
  description: "Provide appropriate substrate orientations, stable scratching posts, and territorial markers.",
  takeaway: "Core focus: vertical and horizontal scent-marking outlets.",
  iconName: "Activity"
}, {
  id: "biting",
  title: "Nipping and biting",
  category: "Safety and Boundaries",
  description: "Manage fear-based defense, play biting, and redirection techniques for all life stages.",
  takeaway: "Core focus: immediate disengagement and impulse training.",
  iconName: "AlertTriangle"
}, {
  id: "chewing",
  title: "Destructive chewing",
  category: "Canine Instinct",
  description: "Manage teething discomfort, boredom, and redirect chewing onto safe, durable enrichment items.",
  takeaway: "Core focus: rotating puzzle toys and proactive confinement management.",
  iconName: "Zap"
}, {
  id: "leash-training",
  title: "Leash training",
  category: "Canine Skills",
  description: "Resolve pulling, leash reactivity, and establish relaxed loose-leash walking mechanics.",
  takeaway: "Core focus: pressure-release mechanics and focus engagement.",
  iconName: "Compass"
}, {
  id: "potty-training",
  title: "Potty and litter training",
  category: "House Training",
  description: "Reliable outdoor scheduling, crate hygiene, and litter box substrate preferences.",
  takeaway: "Core focus: enzyme cleaners and timed positive reinforcement.",
  iconName: "Sparkles"
}, {
  id: "socialization",
  title: "Socialization",
  category: "Behavioral Health",
  description: "Controlled, low-stress exposure to unfamiliar animals, people, sounds, and surfaces.",
  takeaway: "Core focus: positive associations without overwhelming the pet.",
  iconName: "Users"
}, {
  id: "sleep",
  title: "Sleep and rest cycles",
  category: "Restorative Care",
  description: "Optimize circadian rhythms, nocturnal restlessness, and comfortable orthopedic rest spaces.",
  takeaway: "Core focus: predictable decompression routines and quiet zones.",
  iconName: "Moon"
}, {
  id: "body-language",
  title: "Body language",
  category: "Communication",
  description: "Read ear postures, tail mechanics, whale eyes, lip licks, and subtle conflict appeasement signals.",
  takeaway: "Core focus: early stress recognition to prevent escalation.",
  iconName: "Eye"
}, {
  id: "multi-pet-homes",
  title: "Multi-pet homes",
  category: "Household Dynamics",
  description: "Humane scent-swapping, resource distribution, and gradual introductions across dogs and cats.",
  takeaway: "Core focus: zero resource guarding through separated feeding zones.",
  iconName: "Users"
}];
function renderTopicIcon(iconName: string) {
  switch (iconName) {
    case "Dog":
      return <Dog className="size-5 text-accent" />;
    case "Compass":
      return <Compass className="size-5 text-accent" />;
    case "Sparkles":
      return <Sparkles className="size-5 text-accent" />;
    case "Heart":
      return <Heart className="size-5 text-accent" />;
    case "ShieldAlert":
      return <ShieldAlert className="size-5 text-accent" />;
    case "Home":
      return <Home className="size-5 text-accent" />;
    case "Volume2":
      return <Volume2 className="size-5 text-accent" />;
    case "MessageSquare":
      return <MessageSquare className="size-5 text-accent" />;
    case "Activity":
      return <Activity className="size-5 text-accent" />;
    case "AlertTriangle":
      return <AlertTriangle className="size-5 text-accent" />;
    case "Zap":
      return <Zap className="size-5 text-accent" />;
    case "Moon":
      return <Moon className="size-5 text-accent" />;
    case "Eye":
      return <Eye className="size-5 text-accent" />;
    case "Users":
      return <Users className="size-5 text-accent" />;
    default:
      return <BookOpen className="size-5 text-accent" />;
  }
}
export default function BehaviorTopics() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="behavior-topics" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial Section Header */}
        <div className="max-w-3xl mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary border border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
            <BellRing className="size-3.5 text-accent" />
            <span>Behavioral Navigation Directory</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mb-4">
            Explore behavior and training guides
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Practical, evidence-backed guidance and humane positive reinforcement protocols. Select any topic to review root causes, stress triggers, and step-by-step modification techniques.
          </p>
        </div>

        {/* Clinical Assurance Bar */}
        <div className="mb-10 p-4 sm:p-5 rounded-xl bg-card border border-border border-l-4 border-l-accent flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3">
            <ShieldAlert className="size-5 text-accent shrink-0 mt-0.5 sm:mt-0" />
            <p className="text-sm font-medium text-foreground">
              <strong className="font-semibold text-foreground">Clinical Governance Rule:</strong> All training advice strictly adheres to force-free, humane positive reinforcement principles verified by certified veterinary behaviorists.
            </p>
          </div>
          <Button asChild variant="outline" size="sm" className="shrink-0 self-start sm:self-auto">
            <Link to="/AboutPetsWhirlPage">Review editorial standards</Link>
          </Button>
        </div>

        {/* 17 Topics Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TOPICS.map((topic, i) => <motion.div key={topic.id} variants={cardVariants} data-index={i} className="h-full">
              <Card className="h-full flex flex-col justify-between border-border bg-card hover:bg-background transition-[color,background-color,border-color,box-shadow] duration-200 hover:border-border hover:shadow-md">
                <CardHeader className="space-y-3 pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="p-2.5 rounded-lg bg-secondary/80 border border-border inline-flex items-center justify-center">
                      {renderTopicIcon(topic.iconName)}
                    </div>
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-background px-2.5 py-1 rounded-md border border-border">
                      {topic.category}
                    </span>
                  </div>
                  <div>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-semibold text-card-foreground">
                      {topic.title}
                    </CardTitle>
                    <CardDescription className="text-xs font-medium text-muted-foreground mt-1">
                      Veterinary Reviewed
                    </CardDescription>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4 py-2 flex-1">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {topic.description}
                  </p>
                  <div className="p-3 rounded-lg bg-secondary/60 border border-border/80 text-xs text-foreground/90 font-medium">
                    {topic.takeaway}
                  </div>
                </CardContent>

                <CardFooter className="pt-4 border-t border-border mt-auto">
                  <Button asChild variant="ghost" size="sm" className="w-full justify-between px-2 text-foreground hover:text-accent group">
                    <Link to="/BehaviorTrainingPage">
                      <span className="text-sm font-semibold">Read behavioral guide</span>
                      <ArrowUpRight className="size-4 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Footer Support Strip */}
        <div className="mt-12 lg:mt-16 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div>
            <h3 className="font-serif text-lg font-semibold text-foreground">
              Need individualized clinical support?
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Sudden behavioral shifts often indicate underlying medical distress or pain.
            </p>
          </div>
          <Button asChild variant="default" size="default">
            <Link to="/PetHealthPage">Explore symptom index</Link>
          </Button>
        </div>
      </div>
    </section>;
}